import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { QuillModule } from 'ngx-quill';
import Quill from 'quill';
import { map } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SupportItem, SupportItemType } from 'src/app/models/support-item.model';
import { QuillService } from 'src/app/services/quill.service';
import { SupportService } from 'src/app/services/support.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { Roles } from 'src/app/utility/role';

interface SupportNavRow {
  item: SupportItem;
  depth: number;
  hasChildren: boolean;
}

const stripHtml = (value: string | null | undefined) =>
  (value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

@Component({
  selector: 'app-support-workspace-dialog',

  templateUrl: './support-workspace.dialog.html',
  styleUrls: ['./support-workspace.dialog.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    ModalShellComponent,
    ReactiveFormsModule,
    QuillModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [QuillService],
})
export class SupportWorkspaceDialogComponent {
  private formBuilder = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private quillService = inject(QuillService);
  private supportService = inject(SupportService);
  private translate = inject(TranslateService);
  private usersService = inject(UserSessionService);

  readonly searchTerm = signal('');
  readonly selectedGuid = signal<string | null>(null);
  readonly editorMode = signal<'create' | 'edit' | null>(null);
  readonly editorType = signal<SupportItemType>('article');
  readonly stripHtml = stripHtml;

  readonly modules = computed(() => this.quillService.modules());
  readonly currentRole = computed(() => this.usersService.role());
  readonly canManage = computed(() => [Roles.superAdmin].includes(this.currentRole()));

  readonly formGroup = this.formBuilder.group({
    guid: [null as string | null],
    title: ['', Validators.required],
    type: ['article' as SupportItemType, Validators.required],
    parentGuid: [null as string | null],
    content: [''],
  });

  readonly items = toSignal(this.supportService.getList().pipe(
    map((items) => [...items].sort((a, b) => a.title.localeCompare(b.title, 'fi'))),
  ), { initialValue: [] as SupportItem[] });

  readonly itemMap = computed(() => new Map(this.items().map((item) => [item.guid, item])));

  readonly childMap = computed(() => {
    const grouped = new Map<string | null, SupportItem[]>();
    this.items().forEach((item) => {
      const key = item.parentGuid ?? null;
      grouped.set(key, [...grouped.get(key) ?? [], item]);
    });

    grouped.forEach((items, key) => {
      grouped.set(key, this.sortItems(items));
    });

    return grouped;
  });

  readonly navRows = computed(() => {
    const rows: SupportNavRow[] = [];
    const grouped = this.childMap();

    const visit = (parentGuid: string | null, depth: number) => {
      const children = grouped.get(parentGuid) ?? [];
      children.forEach((item) => {
        rows.push({
          item,
          depth,
          hasChildren: (grouped.get(item.guid ?? null)?.length ?? 0) > 0,
        });
        visit(item.guid ?? null, depth + 1);
      });
    };

    visit(null, 0);
    return rows;
  });

  readonly categoryRows = computed(() => this.navRows().filter((row) => row.item.type === 'category'));

  readonly selectedItem = computed(() => {
    const guid = this.selectedGuid();
    return guid ? this.itemMap().get(guid) ?? null : null;
  });

  readonly selectedChildren = computed(() => {
    const selected = this.selectedItem();
    if (!selected) {
      return [];
    }
    return this.sortItems(this.childMap().get(selected.guid ?? null) ?? []);
  });

  readonly searchResults = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (term.length === 0) {
      return [];
    }

    return this.items().filter((item) => {
      const haystack = `${item.title} ${stripHtml(item.content)}`.toLowerCase();
      return haystack.includes(term);
    }).slice(0, 12);
  });

  readonly breadcrumb = computed(() => {
    const chain: SupportItem[] = [];
    let current = this.selectedItem();
    const itemMap = this.itemMap();

    while (current) {
      chain.unshift(current);
      current = current.parentGuid ? itemMap.get(current.parentGuid) ?? null : null;
    }

    return chain;
  });

  readonly introCategories = computed(() => this.navRows().filter((row) => row.depth === 0 && row.item.type === 'category'));

  dismiss() {
    this.modalCtrl.dismiss();
  }

  getEditorInstance(editor: Quill) {
    this.quillService.setRef(editor);
  }

  selectItem(item: SupportItem) {
    this.editorMode.set(null);
    this.selectedGuid.set(item.guid ?? null);
  }

  openCreate(type: SupportItemType, parentGuid?: string | null) {
    this.editorMode.set('create');
    this.editorType.set(type);
    this.selectedGuid.set(null);
    this.formGroup.reset({
      guid: null,
      title: '',
      type,
      parentGuid: parentGuid ?? this.getDefaultParentGuid(type),
      content: '',
    });
  }

  openEdit(item: SupportItem) {
    this.editorMode.set('edit');
    this.editorType.set(item.type);
    this.selectedGuid.set(item.guid ?? null);
    this.formGroup.reset({
      guid: item.guid ?? null,
      title: item.title,
      type: item.type,
      parentGuid: item.parentGuid ?? null,
      content: item.content ?? '',
    });
  }

  async saveForm() {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const value = this.formGroup.getRawValue();
    const existing = value.guid ? this.itemMap().get(value.guid) ?? null : null;
    const type = value.type as SupportItemType;
    const content = type === 'article'
      ? this.quillService.quillRef ? this.quillService.getHtml() : value.content ?? ''
      : '';

    const payload: SupportItem = {
      ...existing ?? {},
      guid: value.guid ?? null,
      title: value.title.trim(),
      type,
      parentGuid: value.parentGuid ?? null,
      content,
      updated: Timestamp.now(),
    };

    if (existing?.guid) {
      await this.supportService.update(payload);
      this.selectedGuid.set(existing.guid);
    } else {
      payload.created = Timestamp.now();
      const saved = await this.supportService.save(payload);
      this.selectedGuid.set(saved?.id ?? null);
    }

    this.editorMode.set(null);
  }

  async deleteItem(item: SupportItem) {
    if (item.type === 'category' && (this.childMap().get(item.guid ?? null)?.length ?? 0) > 0) {
      await Dialog.alert({ message: this.translate.instant('supportWorkspace.alerts.deleteChildrenFirst') });
      return;
    }

    const confirmation = await Dialog.confirm({
      title: this.translate.instant('general.delete'),
      message: this.translate.instant('supportWorkspace.alerts.deleteConfirm', { title: item.title }),
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    });
    if (!confirmation.value) {
      return;
    }

    await this.supportService.delete(item);
    if (this.selectedGuid() === item.guid) {
      this.selectedGuid.set(null);
    }
    this.editorMode.set(null);
  }

  cancelEdit() {
    this.editorMode.set(null);
  }

  onSearch(value: string) {
    this.searchTerm.set(value);
  }

  openSearchResult(item: SupportItem) {
    this.searchTerm.set('');
    this.selectItem(item);
  }

  getParentTitle(parentGuid?: string | null) {
    if (!parentGuid) {
      return this.translate.instant('supportWorkspace.fields.root');
    }
    return this.itemMap().get(parentGuid)?.title ?? this.translate.instant('supportWorkspace.fields.root');
  }

  private getDefaultParentGuid(type: SupportItemType) {
    const selected = this.selectedItem();
    if (!selected) {
      return null;
    }
    if (type === 'article') {
      return selected.type === 'category' ? selected.guid ?? null : selected.parentGuid ?? null;
    }
    return selected.type === 'category' ? selected.guid ?? null : selected.parentGuid ?? null;
  }

  private sortItems(items: SupportItem[]) {
    return [...items].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'category' ? -1 : 1;
      }
      return a.title.localeCompare(b.title, 'fi');
    });
  }
}
